import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Clock, Wallet, Star, ArrowRight, Moon } from "lucide-react";
import { api } from "../lib/api";
import { ExpertCard } from "../components/ExpertCard";
import { Button } from "../components/ui/button";
import { useStartChat } from "../hooks/useStartChat";

const HERO_IMG = "https://images.unsplash.com/photo-1621923647893-901f834b3e6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STEPS = [
  { icon: Wallet, title: "Rechargez vos crédits", text: "Choisissez un pack prépayé. Aucun abonnement, aucun renouvellement automatique." },
  { icon: Sparkles, title: "Choisissez votre praticien", text: "Filtrez par spécialité, langue, tarif et disponibilité immédiate." },
  { icon: Clock, title: "Échangez en direct", text: "La facturation ne démarre qu'après l'acceptation de l'expert, au tarif à la minute." },
  { icon: Star, title: "Laissez un avis vérifié", text: "Partagez votre ressenti après une consultation terminée." },
];

export default function Home() {
  const [experts, setExperts] = useState([]);
  const [paymentsLive, setPaymentsLive] = useState(false);
  const navigate = useNavigate();
  const startChat = useStartChat();

  useEffect(() => {
    api.listExperts({ sort: "rating" }).then(({ data }) => setExperts(data.slice(0, 6))).catch(() => {});
    api.config().then(({ data }) => setPaymentsLive(!!data.payments_live)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-celeste-or/30 bg-celeste-or/10 px-3 py-1 text-xs text-celeste-or">
              <Moon className="h-3.5 w-3.5" /> Nouvelle plateforme Essences Céleste · Guidance en direct
            </span>
            <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight text-celeste-ivoire sm:text-5xl lg:text-6xl">
              Une <span className="gold-text">guidance céleste</span>, quand vous en avez besoin.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-celeste-lilas/85">
              Médiums, cartomanciens, tarologues et astrologues vous accompagnent par chat, en toute discrétion.
              Réservé aux personnes majeures, à visée de réflexion et de divertissement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-explore-btn" onClick={() => navigate("/experts")} className="gold-btn rounded-full px-6 font-semibold">
                Découvrir la plateforme bêta <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button data-testid="hero-how-btn" variant="ghost" onClick={() => navigate("/comment-ca-marche")}
                className="rounded-full border border-celeste-violet/40 px-6 text-celeste-lilas hover:bg-celeste-violet/15">
                Comment ça marche
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-celeste-lilas/70">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-celeste-or" /> {paymentsLive ? "Paiement sécurisé" : "Ouverture progressive de la plateforme"}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-celeste-or" /> Facturation à la minute</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="relative">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-celeste-violet/20 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-celeste-or/20">
              <img src={HERO_IMG} alt="Univers céleste" className="h-[420px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-celeste-night/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4">
                <p className="text-sm text-celeste-ivoire">« Les étoiles inclinent, elles ne contraignent pas. »</p>
                <p className="mt-1 text-xs text-celeste-or">Un espace de réflexion, jamais de certitude.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-serif text-2xl text-celeste-ivoire sm:text-3xl">Experts disponibles maintenant</h2>
            <p className="mt-1 text-sm text-celeste-lilas/70">Nos praticiens les mieux notés, prêts à vous répondre.</p>
          </div>
          <Link to="/experts" data-testid="see-all-experts" className="hidden text-sm text-celeste-or hover:underline sm:block">Tout voir →</Link>
        </div>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {experts.map((e) => <ExpertCard key={e.id} expert={e} onStart={startChat} />)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <h2 className="text-center font-serif text-2xl text-celeste-ivoire sm:text-3xl">Comment se déroule une consultation</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={i} className="glass rounded-3xl p-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-celeste-or/30 bg-celeste-or/10">
                <s.icon className="h-6 w-6 text-celeste-or" />
              </div>
              <h3 className="mt-4 font-serif text-lg text-celeste-ivoire">{s.title}</h3>
              <p className="mt-2 text-sm text-celeste-lilas/75">{s.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
